/**
 * Since we use the same Prismic repository for both the website and the blog,
 * we need a way to distinguish between the two when rendering a document link
 * (e.g. inside the blog, we don't want to render a Gatsby Link with a relative
 * path pointing to a page that only exists on the website). We achieve this by
 * having separate link resolvers and returning 'UNKNOWN_DOCUMENT_TYPE' in the
 * default case. If we see this value, we know that no document matched the
 * provided type for this environment, so it must be a page in the other
 * environment, in which case we render a standard external link with an
 * absolute URL.
 */
import {definedSocialClickIds, parseUTMParams} from '@commercial-website/analytics'
import {localeCodeToUrl} from '@commercial-website/data-layer/locale'
import React, {useEffect, useState} from 'react'

import type {ElementType} from '@/analytics/segment'
import {ctaClicked, elementClicked} from '@/analytics/segment'
import ExternalLink from '@/components/external-link'
import {LocalizedLink} from '@/components/localized-link'
import {socialClickIdCookies} from '@/config/cookies'
import type {StateProps} from '@/hooks/use-navigate'
import type {LinkResolver} from '@/types/link-resolver'
import {linkResolver as websiteLinkResolver} from '@/utils/link-resolver'

import type {Link} from './utils'
import {standardizeLinkFormat, isImageLink, isWebLink, isFileLink, isDocumentLink} from './utils'

export type Props = Link & {
    className?: string
    download?: string
    onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
    state?: StateProps
}

// All the possible environments. These are injected via Webpack
type Environment = 'website'

type SiteConfig = {
    resolver: LinkResolver
    url: string
}

type LinkResolverMap = Record<Environment, SiteConfig>

const linkResolverMap: LinkResolverMap = {
    website: {
        resolver: websiteLinkResolver,
        url: 'https://www.pleo.io',
    },
}
const environment = process.env.ENVIRONMENT as Environment
const siteConfig = linkResolverMap[environment]

const PrismicLink = React.forwardRef<HTMLAnchorElement, React.PropsWithChildren<Props>>(
    ({children, className, onClick, ...linkRest}, ref) => {
        if (!linkRest) {
            throw new Error(`Missing link: ${JSON.stringify(linkRest)}`)
        }

        const link = standardizeLinkFormat(linkRest)

        if (isImageLink(link) || isFileLink(link)) {
            const {url, link_type, ...rest} = link

            return (
                <ExternalLink to={url} className={className} onClick={onClick} ref={ref} {...rest}>
                    {children}
                </ExternalLink>
            )
        }

        if (isWebLink(link)) {
            const {url, target, type, link_type, lang, uid, ...rest} = link

            return (
                <ExternalLink
                    to={url}
                    target={target || '_self'}
                    className={className}
                    onClick={onClick}
                    ref={ref}
                    {...rest}
                >
                    {children}
                </ExternalLink>
            )
        }

        if (isDocumentLink(link)) {
            const {type, lang, uid, link_type, url, ...rest} = link

            if (type === 'broken_type') {
                throw new Error(`Broken link: ${JSON.stringify(link)}`)
            }

            // If the link resolver for the current environment fails,
            // we assume it's a document that doesn't exist in the current environment
            // and we check if it's a blog post or a blog homepage
            if (siteConfig?.resolver(link) === 'UNKNOWN_DOCUMENT_TYPE') {
                const blogUrlsMap: Record<string, string> = {
                    blog_homepage: `https://blog.pleo.io/${localeCodeToUrl(link.lang)}
                    `,
                    blog_post: `https://blog.pleo.io/${localeCodeToUrl(link.lang)}/${link.uid}`,
                }

                if (!blogUrlsMap[type]) {
                    throw new Error(`No document found for link: ${JSON.stringify(link)}`)
                }

                return (
                    <ExternalLink
                        to={blogUrlsMap[type]}
                        target="_blank"
                        className={className}
                        onClick={onClick}
                        ref={ref}
                        {...rest}
                    >
                        {children}
                    </ExternalLink>
                )
            }

            // If the link resolver for the current environment has a match,
            // we're safe to render a local, relative path link

            return (
                <LocalizedLink
                    rawPath
                    to={siteConfig?.resolver(link)}
                    className={className}
                    onClick={onClick}
                    ref={ref}
                    {...rest}
                >
                    {children}
                </LocalizedLink>
            )
        }

        return null
    },
)
type ElementTypeUnion = `${ElementType}`

type TrackedPrismicLinkProps = Props & {
    trackingData: {
        linkText: string
        linkType: 'cta' | 'element'
        elementLocation: string
        elementType: ElementTypeUnion
    }
    forwardUTMParams?: boolean
}

export const TrackedPrismicLink = React.forwardRef<
    HTMLAnchorElement,
    React.PropsWithChildren<TrackedPrismicLinkProps>
>(({trackingData, forwardUTMParams, onClick, children, ...rest}, ref) => {
    const initialUrl = ('url' in rest && rest.url) || ''
    const [url, setUrl] = useState(initialUrl)

    useEffect(() => {
        if (!forwardUTMParams) return
        if (!initialUrl.startsWith('http://') && !initialUrl.startsWith('https://')) {
            return
        }

        try {
            const baseUrl = new URL(initialUrl)
            const utmParams = parseUTMParams()
            for (const [key, value] of Object.entries(utmParams)) {
                baseUrl.searchParams.set(key, value)
            }
            setUrl(baseUrl.toString())
        } catch {
            console.warn('Invalid URL passed to TrackedPrismicLink:', initialUrl)
        }
    }, [forwardUTMParams, initialUrl])

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const trackingPayload = {
            elementLocation: trackingData.elementLocation,
            elementType: trackingData.elementType as ElementType,
            elementText: trackingData.linkText,
            destinationUrl: 'url' in rest ? rest.url : undefined,
        }

        if (trackingData.linkType === 'cta') {
            ctaClicked({
                ...trackingPayload,
                ...definedSocialClickIds(socialClickIdCookies),
            })
        } else {
            elementClicked(trackingPayload)
        }

        onClick?.(e)
    }

    return (
        <PrismicLink ref={ref} onClick={handleClick} {...rest} url={url}>
            {children}
        </PrismicLink>
    )
})

export default PrismicLink
